import React, { useState, useEffect } from 'react';
import axios from './lib/axiosClient';

const COLORS = ['#ff6b2b', '#ffab40', '#00b894', '#6c5ce7', '#e17055', '#0984e3', '#fdcb6e', '#d63031'];
const STATUS_COLORS = {
  received:'#6366f1', diagnosed:'#f59e0b', approved:'#10b981', in_progress:'#3b82f6',
  quality_check:'#8b5cf6', ready:'#059669', picked_up:'#64748b', closed:'#94a3b8',
};

function Reports() {
  const [tab, setTab] = useState('shop');
  const [stats, setStats] = useState({ todayRevenue:0, monthRevenue:0, todayBookings:0, totalCustomers:0 });
  const [revenueData, setRevenueData] = useState([]);
  const [topServices, setTopServices] = useState([]);
  const [busiestHours, setBusiestHours] = useState([]);
  const [revenuePeriod, setRevenuePeriod] = useState(7);
  const [shopData, setShopData] = useState(null);
  const [shopPeriod, setShopPeriod] = useState(30);
  const [loading, setLoading] = useState(true);
  const [commData, setCommData] = useState(null);
  const [commRange, setCommRange] = useState('month');

  useEffect(() => { loadAll(); }, []);
  useEffect(() => { loadRevenue(); }, [revenuePeriod]);
  useEffect(() => { loadShopReports(); }, [shopPeriod]);
  useEffect(() => { loadCommissions(); }, [commRange]);

  const loadCommissions = async () => {
    try {
      const res = await axios.get(`/api/staff/reports/commissions?range=${commRange}`);
      if (res.data?.success) setCommData(res.data);
    } catch (e) { console.error(e); }
  };

  const loadAll = async () => {
    try {
      const [statsRes, servRes, hoursRes] = await Promise.all([
        axios.get('/api/dashboard/stats'),
        axios.get('/api/dashboard/top-services?limit=6'),
        axios.get('/api/dashboard/busiest-hours'),
      ]);
      if (statsRes.data?.success) setStats(statsRes.data.stats);
      if (servRes.data?.success) setTopServices(servRes.data.services || []);
      if (hoursRes.data?.success) setBusiestHours(hoursRes.data.data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const loadRevenue = async () => {
    try {
      const res = await axios.get(`/api/dashboard/revenue-chart?days=${revenuePeriod}`);
      if (res.data?.success) setRevenueData(res.data.data || []);
    } catch (e) { console.error(e); }
  };

  const loadShopReports = async () => {
    try {
      const res = await axios.get(`/api/dashboard/shop-reports?days=${shopPeriod}`);
      if (res.data?.success) setShopData(res.data.reports);
    } catch (e) { console.error(e); }
  };

  const maxRevenue = Math.max(...revenueData.map(d => d.revenue), 1);
  const maxHour = Math.max(...busiestHours.map(d => d.bookings), 1);
  const maxService = Math.max(...topServices.map(s => s.count), 1);
  const fmt = (n) => n >= 1000 ? '$' + (n/1000).toFixed(1) + 'k' : '$' + (n||0).toFixed(0);
  const fmtDate = (d) => new Date(d + 'T12:00:00').toLocaleDateString('en-US', { month:'short', day:'numeric' });

  if (loading) return <div style={{display:'flex',alignItems:'center',justifyContent:'center',minHeight:'60vh',color:'#888'}}><p>Loading reports...</p></div>;

  return (
    <div style={{padding:0,maxWidth:'100%'}}>
      <div style={{marginBottom:28}}>
        <h1 style={{fontSize:26,fontWeight:800,color:'#fff',margin:'0 0 6px'}}>Reports & Analytics</h1>
        <p style={{fontSize:14,color:'#888',margin:0}}>Track revenue, performance, and shop metrics</p>
      </div>

      {/* Tab Toggle */}
      <div style={{display:'flex',gap:4,background:'#1a1c2e',borderRadius:10,padding:4,marginBottom:24,width:'fit-content'}}>
        {[{k:'shop',l:'🔧 Shop'},{k:'bookings',l:'📅 Bookings'},{k:'commissions',l:'💰 Commissions'},{k:'export',l:'📥 Export'}].map(t => (
          <button key={t.k} onClick={() => setTab(t.k)} style={{
            padding:'10px 24px',borderRadius:8,border:'none',fontSize:14,fontWeight:700,cursor:'pointer',
            background:tab===t.k?'#ff6b2b':'transparent', color:tab===t.k?'#fff':'#888',
          }}>{t.l}</button>
        ))}
      </div>

      {/* ============================================================ */}
      {/* SHOP TAB */}
      {/* ============================================================ */}
      {tab === 'shop' && shopData && (<>
        <div style={{display:'flex',gap:6,marginBottom:20}}>
          {[7,14,30,90].map(d => (
            <button key={d} onClick={() => setShopPeriod(d)} style={{
              padding:'8px 18px',borderRadius:8,fontSize:13,fontWeight:600,cursor:'pointer',
              background:shopPeriod===d?'#ff6b2b':'#12121f',color:shopPeriod===d?'#fff':'#888',
              border:shopPeriod===d?'none':'1px solid #1e1e35',
            }}>{d}d</button>
          ))}
        </div>

        {/* Revenue Cards */}
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))',gap:16,marginBottom:24}}>
          {[
            {label:'WO Revenue',value:fmt(shopData.revenue.total),color:'#ff6b2b',icon:'💰'},
            {label:'Collected',value:fmt(shopData.revenue.paid),color:'#00b894',icon:'✅'},
            {label:'Outstanding',value:fmt(shopData.revenue.outstanding),color:shopData.revenue.outstanding>0?'#ef4444':'#00b894',icon:'⏳'},
            {label:'Labor Revenue',value:fmt(shopData.revenue.labor),color:'#3b82f6',icon:'🔧'},
            {label:'Parts Revenue',value:fmt(shopData.revenue.parts),color:'#f59e0b',icon:'⚙️'},
            {label:'Completed WOs',value:shopData.workOrders.completed,color:'#6c5ce7',icon:'📋'},
            {label:'Avg Turnaround',value:shopData.workOrders.avgTurnaroundHours < 24 ? shopData.workOrders.avgTurnaroundHours + 'h' : Math.round(shopData.workOrders.avgTurnaroundHours/24*10)/10 + 'd',color:'#0984e3',icon:'⏱️'},
            {label:'Mechanic Hours',value:shopData.mechanics.totalHours + 'h',color:'#e17055',icon:'👨‍🔧'},
          ].map((s,i) => (
            <div key={i} style={{background:'#12121f',border:'1px solid #1e1e35',borderRadius:16,padding:20,position:'relative',overflow:'hidden'}}>
              <div style={{position:'absolute',top:10,right:14,fontSize:24,opacity:0.12}}>{s.icon}</div>
              <p style={{fontSize:11,color:'#888',fontWeight:600,marginBottom:6,textTransform:'uppercase',letterSpacing:'0.5px'}}>{s.label}</p>
              <p style={{fontSize:26,fontWeight:800,color:s.color,margin:0}}>{s.value}</p>
            </div>
          ))}
        </div>

        {/* Status + Mechanics */}
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(340px,1fr))',gap:20,marginBottom:24}}>
          <div style={{background:'#12121f',border:'1px solid #1e1e35',borderRadius:20,padding:28}}>
            <h2 style={{fontSize:18,fontWeight:700,color:'#fff',margin:'0 0 4px'}}>Work Orders by Status</h2>
            <p style={{fontSize:13,color:'#666',margin:'0 0 20px'}}>Current pipeline</p>
            {Object.keys(shopData.workOrders.statusCounts).length === 0
              ? <div style={{textAlign:'center',padding:32,color:'#555'}}>No work orders</div>
              : <div style={{display:'flex',flexDirection:'column',gap:10}}>
                  {Object.entries(shopData.workOrders.statusCounts).sort((a,b) => b[1]-a[1]).map(([status,count]) => {
                    const maxC = Math.max(...Object.values(shopData.workOrders.statusCounts),1);
                    const color = STATUS_COLORS[status]||'#888';
                    return (
                      <div key={status} style={{display:'flex',alignItems:'center',gap:12}}>
                        <span style={{fontSize:13,fontWeight:600,color:'#888',width:100,textAlign:'right',flexShrink:0,textTransform:'capitalize'}}>{status.replace(/_/g,' ')}</span>
                        <div style={{flex:1,height:28,background:'#1a1a30',borderRadius:8,overflow:'hidden',position:'relative'}}>
                          <div style={{height:'100%',borderRadius:8,background:color,width:`${(count/maxC)*100}%`,transition:'width 0.5s',display:'flex',alignItems:'center',justifyContent:'flex-end',paddingRight:10}}>
                            {count/maxC>0.2 && <span style={{fontSize:12,fontWeight:700,color:'#fff'}}>{count}</span>}
                          </div>
                          {count/maxC<=0.2 && <span style={{position:'absolute',left:`${(count/maxC)*100+2}%`,top:'50%',transform:'translateY(-50%)',fontSize:12,fontWeight:700,color}}>{count}</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
            }
          </div>

          <div style={{background:'#12121f',border:'1px solid #1e1e35',borderRadius:20,padding:28}}>
            <h2 style={{fontSize:18,fontWeight:700,color:'#fff',margin:'0 0 4px'}}>Mechanic Hours</h2>
            <p style={{fontSize:13,color:'#666',margin:'0 0 20px'}}>Last {shopPeriod} days</p>
            {shopData.mechanics.leaderboard.length === 0
              ? <div style={{textAlign:'center',padding:32,color:'#555'}}><p style={{fontSize:28,marginBottom:8}}>👨‍🔧</p><p>No time entries yet</p></div>
              : <div style={{display:'flex',flexDirection:'column',gap:12}}>
                  {shopData.mechanics.leaderboard.map((m,i) => {
                    const maxH = Math.max(...shopData.mechanics.leaderboard.map(x=>x.hours),1);
                    return (
                      <div key={i} style={{display:'flex',alignItems:'center',gap:12}}>
                        <div style={{width:32,height:32,borderRadius:8,background:COLORS[i%COLORS.length]+'20',display:'flex',alignItems:'center',justifyContent:'center',fontSize:14,fontWeight:800,color:COLORS[i%COLORS.length],flexShrink:0}}>{i+1}</div>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{display:'flex',justifyContent:'space-between',marginBottom:6}}>
                            <span style={{fontSize:14,fontWeight:600,color:'#e2e2e8'}}>{m.name}</span>
                            <span style={{fontSize:13,fontWeight:700,color:COLORS[i%COLORS.length],flexShrink:0}}>{m.hours}h</span>
                          </div>
                          <div style={{height:6,background:'#1a1a30',borderRadius:3,overflow:'hidden'}}>
                            <div style={{height:'100%',borderRadius:3,background:`linear-gradient(90deg,${COLORS[i%COLORS.length]},${COLORS[i%COLORS.length]}90)`,width:`${(m.hours/maxH)*100}%`,transition:'width 0.6s'}} />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
            }
          </div>
        </div>

        {/* Top Parts */}
        <div style={{background:'#12121f',border:'1px solid #1e1e35',borderRadius:20,padding:28,marginBottom:24}}>
          <h2 style={{fontSize:18,fontWeight:700,color:'#fff',margin:'0 0 4px'}}>Top Parts Used</h2>
          <p style={{fontSize:13,color:'#666',margin:'0 0 20px'}}>Most frequently used parts</p>
          {shopData.parts.topParts.length === 0
            ? <div style={{textAlign:'center',padding:32,color:'#555'}}><p style={{fontSize:28,marginBottom:8}}>⚙️</p><p>No parts used yet</p></div>
            : <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))',gap:12}}>
                {shopData.parts.topParts.map((p,i) => (
                  <div key={i} style={{display:'flex',alignItems:'center',gap:12,padding:14,background:'#1a1c2e',borderRadius:12,border:'1px solid #1e1e35'}}>
                    <div style={{width:40,height:40,borderRadius:10,background:'#f59e0b15',display:'flex',alignItems:'center',justifyContent:'center',fontSize:16,flexShrink:0}}>⚙️</div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:14,fontWeight:600,color:'#e2e2e8',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{p.name}</div>
                      <div style={{fontSize:12,color:'#888'}}>{p.qty} used · ${p.revenue.toFixed(2)} revenue</div>
                    </div>
                  </div>
                ))}
              </div>
          }
        </div>
      </>)}

      {tab === 'shop' && !shopData && (
        <div style={{textAlign:'center',padding:60,color:'#555'}}><p style={{fontSize:36,marginBottom:8}}>📊</p><p>Loading shop reports...</p></div>
      )}

      {/* ============================================================ */}
      {/* BOOKINGS TAB */}
      {/* ============================================================ */}
      {tab === 'bookings' && (<>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))',gap:16,marginBottom:28}}>
          {[
            {label:"Today's Revenue",value:fmt(stats.todayRevenue),color:'#ff6b2b',icon:'💰'},
            {label:'Monthly Revenue',value:fmt(stats.monthRevenue),color:'#00b894',icon:'📈'},
            {label:"Today's Bookings",value:stats.todayBookings,color:'#ffab40',icon:'📅'},
            {label:'Total Customers',value:stats.totalCustomers,color:'#6c5ce7',icon:'👥'},
          ].map((s,i) => (
            <div key={i} style={{background:'#12121f',border:'1px solid #1e1e35',borderRadius:16,padding:24,position:'relative',overflow:'hidden'}}>
              <div style={{position:'absolute',top:12,right:16,fontSize:28,opacity:0.15}}>{s.icon}</div>
              <p style={{fontSize:13,color:'#888',fontWeight:600,marginBottom:8}}>{s.label}</p>
              <p style={{fontSize:32,fontWeight:800,color:s.color,margin:0}}>{s.value}</p>
            </div>
          ))}
        </div>

        <div style={{background:'#12121f',border:'1px solid #1e1e35',borderRadius:20,padding:28,marginBottom:20}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:24,flexWrap:'wrap',gap:12}}>
            <div>
              <h2 style={{fontSize:18,fontWeight:700,color:'#fff',margin:'0 0 4px'}}>Revenue</h2>
              <p style={{fontSize:13,color:'#666',margin:0}}>Daily revenue over time</p>
            </div>
            <div style={{display:'flex',gap:6,background:'#1a1c2e',borderRadius:10,padding:4}}>
              {[7,14,30].map(d => (
                <button key={d} onClick={() => setRevenuePeriod(d)} style={{
                  padding:'8px 16px',borderRadius:8,border:'none',fontSize:13,fontWeight:600,cursor:'pointer',
                  background:revenuePeriod===d?'#ff6b2b':'transparent',color:revenuePeriod===d?'#fff':'#888',
                }}>{d}d</button>
              ))}
            </div>
          </div>
          {revenueData.length === 0 ? (
            <div style={{textAlign:'center',padding:48,color:'#555'}}><p style={{fontSize:36,marginBottom:8}}>📊</p><p>No revenue data yet.</p></div>
          ) : (
            <div style={{position:'relative'}}>
              <div style={{display:'flex',flexDirection:'column',justifyContent:'space-between',position:'absolute',left:0,top:0,bottom:28,width:50}}>
                {[1,0.75,0.5,0.25,0].map((pct,i) => (
                  <span key={i} style={{fontSize:11,color:'#555',textAlign:'right',paddingRight:8}}>{fmt(maxRevenue*pct)}</span>
                ))}
              </div>
              <div style={{marginLeft:54,display:'flex',alignItems:'flex-end',gap:4,height:220,borderBottom:'1px solid #1e1e35',paddingBottom:4}}>
                {revenueData.map((d,i) => (
                  <div key={i} style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'flex-end',height:'100%'}}>
                    <div style={{fontSize:11,color:'#ff6b2b',marginBottom:4,fontWeight:700,opacity:d.revenue>0?1:0}}>{fmt(d.revenue)}</div>
                    <div style={{width:'100%',maxWidth:40,height:`${Math.max((d.revenue/maxRevenue)*100,2)}%`,background:'linear-gradient(180deg,#ff6b2b,#ff6b2b80)',borderRadius:'6px 6px 2px 2px',transition:'height 0.5s'}} />
                  </div>
                ))}
              </div>
              <div style={{marginLeft:54,display:'flex',gap:4,marginTop:8}}>
                {revenueData.map((d,i) => (
                  <div key={i} style={{flex:1,textAlign:'center',fontSize:11,color:'#555'}}>{revenueData.length<=14?fmtDate(d.date):i%3===0?fmtDate(d.date):''}</div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(340px,1fr))',gap:20}}>
          <div style={{background:'#12121f',border:'1px solid #1e1e35',borderRadius:20,padding:28}}>
            <h2 style={{fontSize:18,fontWeight:700,color:'#fff',margin:'0 0 4px'}}>Top Services</h2>
            <p style={{fontSize:13,color:'#666',margin:'0 0 24px'}}>Most booked services</p>
            {topServices.length === 0
              ? <div style={{textAlign:'center',padding:32,color:'#555'}}>No bookings yet</div>
              : <div style={{display:'flex',flexDirection:'column',gap:12}}>
                  {topServices.map((s,i) => (
                    <div key={i} style={{display:'flex',alignItems:'center',gap:12}}>
                      <div style={{width:32,height:32,borderRadius:8,background:COLORS[i%COLORS.length]+'20',display:'flex',alignItems:'center',justifyContent:'center',fontSize:14,fontWeight:800,color:COLORS[i%COLORS.length],flexShrink:0}}>{i+1}</div>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{display:'flex',justifyContent:'space-between',marginBottom:6}}>
                          <span style={{fontSize:14,fontWeight:600,color:'#e2e2e8'}}>{s.service?.name||'Unknown'}</span>
                          <span style={{fontSize:13,fontWeight:700,color:COLORS[i%COLORS.length]}}>{s.count}</span>
                        </div>
                        <div style={{height:6,background:'#1a1a30',borderRadius:3,overflow:'hidden'}}>
                          <div style={{height:'100%',borderRadius:3,background:`linear-gradient(90deg,${COLORS[i%COLORS.length]},${COLORS[i%COLORS.length]}90)`,width:`${(s.count/maxService)*100}%`,transition:'width 0.6s'}} />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
            }
          </div>

          <div style={{background:'#12121f',border:'1px solid #1e1e35',borderRadius:20,padding:28}}>
            <h2 style={{fontSize:18,fontWeight:700,color:'#fff',margin:'0 0 4px'}}>Busiest Hours</h2>
            <p style={{fontSize:13,color:'#666',margin:'0 0 24px'}}>When clients book most</p>
            {busiestHours.length === 0
              ? <div style={{textAlign:'center',padding:32,color:'#555'}}>No data yet</div>
              : <div style={{display:'flex',flexDirection:'column',gap:10}}>
                  {busiestHours.map((h,i) => (
                    <div key={i} style={{display:'flex',alignItems:'center',gap:12}}>
                      <span style={{fontSize:13,fontWeight:600,color:'#888',width:52,textAlign:'right',flexShrink:0}}>{h.hour}</span>
                      <div style={{flex:1,height:28,background:'#1a1a30',borderRadius:8,overflow:'hidden',position:'relative'}}>
                        <div style={{height:'100%',borderRadius:8,background:'linear-gradient(90deg,#ffab40,#ff6b2b)',width:`${(h.bookings/maxHour)*100}%`,transition:'width 0.6s',display:'flex',alignItems:'center',justifyContent:'flex-end',paddingRight:10}}>
                          {h.bookings/maxHour>0.2 && <span style={{fontSize:12,fontWeight:700,color:'#fff'}}>{h.bookings}</span>}
                        </div>
                        {h.bookings/maxHour<=0.2 && <span style={{position:'absolute',left:`${(h.bookings/maxHour)*100+2}%`,top:'50%',transform:'translateY(-50%)',fontSize:12,fontWeight:700,color:'#ffab40'}}>{h.bookings}</span>}
                      </div>
                    </div>
                  ))}
                </div>
            }
          </div>
        </div>
      </>)}

      {/* ============================================================ */}
      {/* COMMISSIONS TAB */}
      {/* ============================================================ */}
      {tab === 'commissions' && (
        <div style={{background:'#12121f',borderRadius:16,padding:28,border:'1px solid #1e1e35'}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:24,flexWrap:'wrap',gap:12}}>
            <h2 style={{color:'#fff',fontSize:20,fontWeight:700,margin:0}}>💰 Staff Commissions</h2>
            <div style={{display:'flex',gap:4,background:'#1a1c2e',borderRadius:8,padding:3}}>
              {[{k:'today',l:'Today'},{k:'week',l:'7 Days'},{k:'month',l:'30 Days'},{k:'all',l:'All Time'}].map(r => (
                <button key={r.k} onClick={() => setCommRange(r.k)} style={{
                  padding:'8px 16px',borderRadius:6,border:'none',fontSize:13,fontWeight:600,cursor:'pointer',
                  background:commRange===r.k?'#ff6b2b':'transparent',color:commRange===r.k?'#fff':'#888',
                }}>{r.l}</button>
              ))}
            </div>
          </div>

          {commData && commData.totals && (
            <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit, minmax(140px, 1fr))',gap:12,marginBottom:24}}>
              <div style={{background:'#1a1c2e',borderRadius:12,padding:16,textAlign:'center'}}>
                <div style={{color:'#888',fontSize:12,marginBottom:4}}>Total Revenue</div>
                <div style={{color:'#10b981',fontSize:24,fontWeight:800}}>${commData.totals.revenue?.toFixed(0) || 0}</div>
              </div>
              <div style={{background:'#1a1c2e',borderRadius:12,padding:16,textAlign:'center'}}>
                <div style={{color:'#888',fontSize:12,marginBottom:4}}>Total Commissions</div>
                <div style={{color:'#ff6b2b',fontSize:24,fontWeight:800}}>${commData.totals.commission?.toFixed(0) || 0}</div>
              </div>
              <div style={{background:'#1a1c2e',borderRadius:12,padding:16,textAlign:'center'}}>
                <div style={{color:'#888',fontSize:12,marginBottom:4}}>Total Tips</div>
                <div style={{color:'#ffab40',fontSize:24,fontWeight:800}}>${commData.totals.tips?.toFixed(0) || 0}</div>
              </div>
              <div style={{background:'#1a1c2e',borderRadius:12,padding:16,textAlign:'center'}}>
                <div style={{color:'#888',fontSize:12,marginBottom:4}}>Services Done</div>
                <div style={{color:'#6366f1',fontSize:24,fontWeight:800}}>{commData.totals.services || 0}</div>
              </div>
            </div>
          )}

          {commData && commData.staff && commData.staff.length > 0 ? (
            <div>
              {commData.staff.map(function(s, i) {
                var maxEarned = Math.max(...commData.staff.map(function(x) { return x.total_earned; }), 1);
                return (
                  <div key={s.id} style={{display:'flex',alignItems:'center',gap:14,padding:'14px 0',borderBottom:i < commData.staff.length - 1 ? '1px solid #1e1e35' : 'none'}}>
                    <div style={{width:32,height:32,borderRadius:10,background:'#ff6b2b',display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontWeight:800,fontSize:14,flexShrink:0}}>
                      {s.name.charAt(0)}
                    </div>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{display:'flex',justifyContent:'space-between',marginBottom:6}}>
                        <div>
                          <span style={{color:'#fff',fontWeight:700,fontSize:15}}>{s.name}</span>
                          <span style={{color:'#888',fontSize:12,marginLeft:8}}>{s.role} · {s.commission_rate}%</span>
                        </div>
                        <div style={{textAlign:'right'}}>
                          <span style={{color:'#10b981',fontWeight:800,fontSize:16}}>${s.total_earned.toFixed(0)}</span>
                          <span style={{color:'#888',fontSize:11,marginLeft:6}}>earned</span>
                        </div>
                      </div>
                      <div style={{display:'flex',gap:4,height:8,borderRadius:4,overflow:'hidden',background:'#1a1c2e'}}>
                        <div style={{width:`${(s.commission/maxEarned)*100}%`,background:'#ff6b2b',borderRadius:4,transition:'width 0.5s'}} title={'Commission: $' + s.commission.toFixed(0)} />
                        <div style={{width:`${(s.tips/maxEarned)*100}%`,background:'#ffab40',borderRadius:4,transition:'width 0.5s'}} title={'Tips: $' + s.tips.toFixed(0)} />
                      </div>
                      <div style={{display:'flex',gap:16,marginTop:6}}>
                        <span style={{color:'#888',fontSize:11}}>💇 {s.services_completed} services</span>
                        <span style={{color:'#888',fontSize:11}}>💵 ${s.revenue.toFixed(0)} revenue</span>
                        <span style={{color:'#ff6b2b',fontSize:11}}>📊 ${s.commission.toFixed(0)} comm</span>
                        <span style={{color:'#ffab40',fontSize:11}}>💰 ${s.tips.toFixed(0)} tips</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{textAlign:'center',padding:'40px 0',color:'#888'}}>
              <div style={{fontSize:40,marginBottom:8}}>💈</div>
              <div>No commission data for this period</div>
            </div>
          )}
        </div>
      )}

      {/* ============================================================ */}
      {/* EXPORT TAB */}
      {/* ============================================================ */}
      {tab === 'export' && (
        <div style={{background:'#12121f',borderRadius:16,padding:28,border:'1px solid #1e1e35'}}>
          <h2 style={{color:'#fff',fontSize:20,fontWeight:700,marginBottom:8}}>📥 Export Your Data</h2>
          <p style={{color:'#888',fontSize:14,marginBottom:24}}>Download your business data as CSV files. Open them in Excel, Google Sheets, or any spreadsheet app.</p>
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit, minmax(240px, 1fr))',gap:16}}>
            {[
              {type:'customers', icon:'👥', title:'Customers', desc:'Names, contacts, visit history, spend totals'},
              {type:'transactions', icon:'💰', title:'Transactions', desc:'All payments, tips, methods, staff'},
              {type:'bookings', icon:'📅', title:'Bookings', desc:'Appointments, services, staff, status'},
              {type:'inventory', icon:'📦', title:'Inventory', desc:'Products, stock levels, pricing, SKUs'},
            ].map(function(item) {
              return (
                <div key={item.type} style={{background:'#1a1c2e',borderRadius:12,padding:20,border:'1px solid #1e1e35'}}>
                  <div style={{fontSize:28,marginBottom:8}}>{item.icon}</div>
                  <div style={{color:'#fff',fontSize:16,fontWeight:700,marginBottom:4}}>{item.title}</div>
                  <div style={{color:'#888',fontSize:13,marginBottom:16,lineHeight:1.5}}>{item.desc}</div>
                  <button onClick={function() { import('./lib/exportCSV').then(function(m) { m.exportCSV(item.type); }); }} style={{
                    width:'100%',padding:'10px',background:'linear-gradient(135deg,#ff6b2b,#ffab40)',border:'none',borderRadius:8,
                    color:'#fff',fontSize:14,fontWeight:700,cursor:'pointer',
                  }}>Download CSV</button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default Reports;
